import React, { isValidElement, Fragment, useState, useEffect } from "react";

import { Tooltip } from "@mui/material";
import { BiImport, BiExport } from "react-icons/bi";
import { FiPrinter } from "react-icons/fi";
import { GoMail } from "react-icons/go";

import { GeneralActions, Constants, OrderBy, Column, ColumnFilter } from "../export";
import FilterBy from "./FilterBy";
import NewRecord from "./NewRecord";
import SearchSuggest from "./SearchSuggest";
import SelectDate from "./SelectDate";
import useStyles from "./styles";

type Props = {
  columns?: Column[];
  actions?: GeneralActions;
  titlePage?: string;
  selectedList: any[];
};

const DEFAULT_ORDER_BY: OrderBy = {
  left: ["handleSelectDate", "handleImport", "handlePrintAll", "handleExport", "handleSendEmail"],
  right: ["handleSearch", "handleFilterBy", "handleNewRecord"],
};

const TopPanel = ({ columns, actions, titlePage, selectedList }: Props) => {
  const classes = useStyles();

  const [inputSearch, setInputSearch] = useState<string>("");

  const [columnsSelected, setColumnsSelected] = useState<ColumnFilter[]>([]);

  const [orderBy, setOrderBy] = useState<OrderBy>();

  useEffect(() => {
    setOrderBy(actions?.orderBy || DEFAULT_ORDER_BY);
  }, [actions?.orderBy]);

  useEffect(() => {
    sendSearch();
  }, [columnsSelected]);

  const sendSearch = () => {
    const objectFilterUpdated: Array<{ [x: string]: string }> = [];

    if (columnsSelected && columnsSelected?.length > 0) {
      columnsSelected?.forEach((column) => {
        if (column?.name || column?.title || column?.searchIdentifier) {
          objectFilterUpdated.push({
            [column?.searchIdentifier || column?.name || column?.title?.toLowerCase()]:
              column?.value || "",
          });
        }
      });
    }
    if (actions?.handleSearch?.onSearch)
      actions?.handleSearch?.onSearch(objectFilterUpdated, inputSearch);
  };

  const renderSearch = () => {
    if (actions?.handleSearch) {
      return (
        <SearchSuggest
          columns={columns}
          inputValue={inputSearch}
          onChangeInput={(value) => setInputSearch(value)}
          placeholder={actions?.handleSearch?.inputPlaceholder || "Search"}
          columnsSelected={columnsSelected}
          onChangeColumnsSelected={(columns) => setColumnsSelected(columns)}
        />
      );
    }
  };

  const renderNewRecord = () => {
    if (actions?.handleNewRecord) {
      return <NewRecord data-cy="new-record" newRecord={actions?.handleNewRecord} />;
    }
  };

  const renderFilterBy = () => {
    if (actions?.handleFilterBy) {
      return <FilterBy data-cy="filter" filter={actions?.handleFilterBy} />;
    }
  };

  const renderDateSelect = () => {
    if (actions?.handleSelectDate) {
      return <SelectDate data-cy="select-date" {...actions.handleSelectDate} />;
    }
  };

  const renderHandleImport = () => {
    if (actions?.handleImport) {
      return (
        <Fragment data-cy="import" key={"uploadCsv"}>
          <input
            accept=".csv"
            className="d-none"
            style={{ visibility: "hidden", width: "1px" }}
            id="import-csv-file"
            type="file"
            onChange={() => actions.handleImport && actions.handleImport(selectedList)}
          />
          <Tooltip id="import-csv-file-tool" title={"Import file"} placement="top">
            <div>
              <BiExport
                size={Constants.defaultGeneralActionsIconSize}
                color={Constants.defaultColorIcon}
              />
            </div>
          </Tooltip>
        </Fragment>
      );
    }
  };

  const renderPrintAll = () => {
    if (actions?.handlePrintAll) {
      return (
        <Tooltip data-cy="print" placement="top" title={"Print selected"}>
          <div>
            <FiPrinter
              size={Constants.defaultGeneralActionsIconSize}
              color={Constants.defaultColorIcon}
              onClick={() => actions.handlePrintAll && actions.handlePrintAll(selectedList)}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const renderExport = () => {
    if (actions?.handleExport) {
      return (
        <Tooltip data-cy="export" title={"Export Shipment"} placement="top">
          <div>
            <BiImport
              size={Constants.defaultGeneralActionsIconSize}
              color={Constants.defaultColorIcon}
              onClick={() => actions.handleExport && actions.handleExport(selectedList)}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const renderSendEmail = () => {
    if (actions?.handleSendEmail) {
      return (
        <Tooltip data-cy="send-email" title={"Send Email"} placement="top">
          <div>
            <GoMail
              size={Constants.defaultGeneralActionsIconSize}
              color={Constants.defaultColorIcon}
              onClick={() => actions.handleSendEmail && actions.handleSendEmail(selectedList)}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const getComponentByAction = (action: string) => {
    if (actions) {
      switch (action) {
        case "handleNewRecord":
          return renderNewRecord();
        case "handleSearch":
          return renderSearch();
        case "handleFilterBy":
          return renderFilterBy();
        case "handlePrintAll":
          return renderPrintAll();
        case "handleImport":
          return renderHandleImport();
        case "handleExport":
          return renderExport();
        case "handleSendEmail":
          return renderSendEmail();
        case "handleSelectDate":
          return renderDateSelect();
        default:
          return actions[action] && isValidElement(actions[action]) ? actions[action] : null;
      }
    }
  };

  const renderLeftActions = () => {
    if (orderBy && orderBy?.left && orderBy?.left?.length > 0) {
      return orderBy?.left?.map((action, index) => {
        return (
          <div
            key={action}
            data-cy={action.toLowerCase()}
            className={index === 0 ? undefined : classes.leftItem}
          >
            {getComponentByAction(action)}
          </div>
        );
      });
    }

    return null;
  };

  const renderRightActions = () => {
    if (orderBy && orderBy?.right && orderBy?.right?.length > 0) {
      return orderBy?.right?.map((action, index, array) => {
        return (
          <div
            key={action}
            data-cy={action.toLowerCase()}
            className={index + 1 === array.length ? undefined : classes.rightItem}
          >
            {getComponentByAction(action)}
          </div>
        );
      });
    }

    return null;
  };

  const renderTitle = () => {
    if (titlePage) {
      return (
        <span data-cy="title" className={classes.title}>
          {titlePage}
        </span>
      );
    }

    return null;
  };

  return (
    <div data-cy="top-panel" className={classes.topPanel}>
      <div data-cy="left-side" className={classes.leftSide}>
        {renderTitle()}
        {renderLeftActions()}
      </div>
      {renderRightActions()}
    </div>
  );
};

export default TopPanel;
